import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebase";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import "./AdminGuestFamilySummary.scss";

const GROOM_COLLECTION = "GroomGuests";
const BRIDE_COLLECTION = "BrideGuests";

const EMPTY_SUMMARY = {
  loading: false,
  error: "",
  brideFamilies: [],
  groomFamilies: [],
};

const normalizeFamilyNameForUnique = (name) =>
  String(name || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const getFamilyName = (familyData, fallbackId) =>
  String(
    familyData?.familyName ||
      familyData?.family_name ||
      familyData?.name ||
      familyData?.family ||
      fallbackId ||
      ""
  ).trim();

const getGuestName = (guest, index) => {
  if (typeof guest === "string") return guest;

  return (
    guest?.guestName ||
    guest?.name ||
    guest?.fullName ||
    guest?.guest_name ||
    guest?.familyMemberName ||
    `Guest ${index + 1}`
  );
};

const getGuestLiquorStatus = (guest) => {
  if (!guest || typeof guest === "string") return "";

  const value =
    guest?.isTakingLiquor ??
    guest?.takingLiquor ??
    guest?.liquor ??
    guest?.isLiquor ??
    guest?.drinkLiquor;

  if (value === true) return "Taking liquor";
  if (value === false) return "No liquor";

  return "";
};

const getGuestsInsideFamily = (familyData) => {
  const possibleArrays = [
    familyData?.guests,
    familyData?.members,
    familyData?.familyMembers,
    familyData?.guestList,
    familyData?.guestNames,
  ];

  const matchedArray = possibleArrays.find(Array.isArray);

  if (!matchedArray) {
    return [];
  }

  return matchedArray.map((guest, index) => ({
    id: guest?.id || guest?.guestId || `${index}`,
    name: getGuestName(guest, index),
    liquorStatus: getGuestLiquorStatus(guest),
    raw: guest,
  }));
};

const getGuestCount = (familyData) => {
  const guests = getGuestsInsideFamily(familyData);
  if (guests.length > 0) return guests.length;

  const possibleNumberFields = [
    familyData?.guestCount,
    familyData?.guestsCount,
    familyData?.count,
    familyData?.numberOfGuests,
    familyData?.totalGuests,
  ];

  const matchedNumber = possibleNumberFields.find((value) =>
    Number.isFinite(Number(value))
  );

  if (matchedNumber !== undefined) return Number(matchedNumber);

  return 1;
};

const mapFamilySnapshot = (snapshot, side) =>
  snapshot.docs
    .map((familyDoc) => {
      const familyData = familyDoc.data();
      const familyName = getFamilyName(familyData, familyDoc.id);

      if (!familyName) return null;

      return {
        id: familyDoc.id,
        side,
        familyName,
        guestCount: getGuestCount(familyData),
        guests: getGuestsInsideFamily(familyData),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.familyName.localeCompare(b.familyName));

const AdminGuestFamilySummary = ({ selectedUser }) => {
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [copyStatus, setCopyStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFamily, setSelectedFamily] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadGuestFamilies = async () => {
      if (!selectedUser?.id) {
        setSummary(EMPTY_SUMMARY);
        return;
      }

      try {
        setSummary({
          loading: true,
          error: "",
          brideFamilies: [],
          groomFamilies: [],
        });

        setCopyStatus("");
        setSearchTerm("");
        setSelectedFamily(null);

        const [brideSnapshot, groomSnapshot] = await Promise.all([
          getDocs(collection(db, "users", selectedUser.id, BRIDE_COLLECTION)),
          getDocs(collection(db, "users", selectedUser.id, GROOM_COLLECTION)),
        ]);

        if (!isMounted) return;

        setSummary({
          loading: false,
          error: "",
          brideFamilies: mapFamilySnapshot(brideSnapshot, "Bride Side"),
          groomFamilies: mapFamilySnapshot(groomSnapshot, "Groom Side"),
        });
      } catch (error) {
        console.error("Failed to load guest families:", error);

        if (!isMounted) return;

        setSummary({
          loading: false,
          error: "Failed to load guest families.",
          brideFamilies: [],
          groomFamilies: [],
        });
      }
    };

    loadGuestFamilies();

    return () => {
      isMounted = false;
    };
  }, [selectedUser?.id]);

  const brideGuestCount = useMemo(
    () =>
      summary.brideFamilies.reduce(
        (total, family) => total + Number(family.guestCount || 0),
        0
      ),
    [summary.brideFamilies]
  );

  const groomGuestCount = useMemo(
    () =>
      summary.groomFamilies.reduce(
        (total, family) => total + Number(family.guestCount || 0),
        0
      ),
    [summary.groomFamilies]
  );

  const getUniqueFamilyNamesText = (families) => {
    const uniqueMap = new Map();

    families.forEach((family) => {
      const key = normalizeFamilyNameForUnique(family.familyName);
      if (!key || uniqueMap.has(key)) return;
      uniqueMap.set(key, family.familyName.trim());
    });

    return Array.from(uniqueMap.values()).join("\n");
  };

  const brideFamilyNamesText = useMemo(
    () => getUniqueFamilyNamesText(summary.brideFamilies),
    [summary.brideFamilies]
  );

  const groomFamilyNamesText = useMemo(
    () => getUniqueFamilyNamesText(summary.groomFamilies),
    [summary.groomFamilies]
  );

  const filteredBrideFamilies = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return summary.brideFamilies;

    return summary.brideFamilies.filter((family) =>
      family.familyName.toLowerCase().includes(term)
    );
  }, [summary.brideFamilies, searchTerm]);

  const filteredGroomFamilies = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return summary.groomFamilies;

    return summary.groomFamilies.filter((family) =>
      family.familyName.toLowerCase().includes(term)
    );
  }, [summary.groomFamilies, searchTerm]);

  const handleCopyFamilyNames = async (type) => {
    const text = type === "bride" ? brideFamilyNamesText : groomFamilyNamesText;
    const label = type === "bride" ? "Bride side" : "Groom side";

    if (!text) {
      setCopyStatus(`No ${label.toLowerCase()} family names to copy.`);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(`${label} family names copied.`);
    } catch (error) {
      console.error("Failed to copy family names:", error);
      setCopyStatus(`Failed to copy ${label.toLowerCase()} family names.`);
    }
  };

  const renderFamilyList = (families, emptyMessage) => {
    if (families.length === 0) {
      return <p className="adminGuestFamilySummary__empty">{emptyMessage}</p>;
    }

    return (
      <ul className="adminGuestFamilySummary__list">
        {families.map((family) => (
          <li
            key={family.id}
            className="adminGuestFamilySummary__listItem"
            onClick={() => setSelectedFamily(family)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                setSelectedFamily(family);
              }
            }}
          >
            <span>{family.familyName}</span>
            <small>
              {family.guestCount} {family.guestCount === 1 ? "guest" : "guests"}
            </small>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section className="adminGuestFamilySummary">
      <div className="adminGuestFamilySummary__header">
        <div>
          <p className="adminGuestFamilySummary__eyebrow">Guest Families</p>
          <h3 className="adminGuestFamilySummary__title">
            Already Added Family Names
          </h3>
        </div>

        <div className="adminGuestFamilySummary__actions">
          <ButtonComponent
            onClick={() => handleCopyFamilyNames("bride")}
            disabled={summary.loading || !brideFamilyNamesText}
          >
            Copy Bride Families
          </ButtonComponent>

          <ButtonComponent
            onClick={() => handleCopyFamilyNames("groom")}
            disabled={summary.loading || !groomFamilyNamesText}
          >
            Copy Groom Families
          </ButtonComponent>
        </div>
      </div>

      <input
        type="text"
        className="adminGuestFamilySummary__search"
        placeholder="Search family name..."
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
      />

      {copyStatus && (
        <p className="adminGuestFamilySummary__copyStatus">{copyStatus}</p>
      )}

      {summary.loading && (
        <p className="adminGuestFamilySummary__message">
          Loading guest families...
        </p>
      )}

      {summary.error && (
        <p className="adminGuestFamilySummary__error">{summary.error}</p>
      )}

      {!summary.loading && !summary.error && (
        <>
          <div className="adminGuestFamilySummary__stats">
            <div className="adminGuestFamilySummary__statCard">
              <span>All Guests</span>
              <strong>{brideGuestCount + groomGuestCount}</strong>
            </div>

            <div className="adminGuestFamilySummary__statCard">
              <span>Bride Side Guests</span>
              <strong>{brideGuestCount}</strong>
            </div>

            <div className="adminGuestFamilySummary__statCard">
              <span>Groom Side Guests</span>
              <strong>{groomGuestCount}</strong>
            </div>
          </div>

          <div className="adminGuestFamilySummary__columns">
            <div className="adminGuestFamilySummary__box">
              <div className="adminGuestFamilySummary__boxHeader">
                <h4>Bride Side Families</h4>
                <span>
                  {filteredBrideFamilies.length} / {summary.brideFamilies.length}{" "}
                  families
                </span>
              </div>

              {renderFamilyList(
                filteredBrideFamilies,
                searchTerm
                  ? "No matching bride side families found."
                  : "No bride side families added yet."
              )}
            </div>

            <div className="adminGuestFamilySummary__box">
              <div className="adminGuestFamilySummary__boxHeader">
                <h4>Groom Side Families</h4>
                <span>
                  {filteredGroomFamilies.length} / {summary.groomFamilies.length}{" "}
                  families
                </span>
              </div>

              {renderFamilyList(
                filteredGroomFamilies,
                searchTerm
                  ? "No matching groom side families found."
                  : "No groom side families added yet."
              )}
            </div>
          </div>
        </>
      )}

      {selectedFamily && (
        <div
          className="adminGuestFamilySummary__guestModalOverlay"
          onClick={() => setSelectedFamily(null)}
        >
          <div
            className="adminGuestFamilySummary__guestModal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="adminGuestFamilySummary__guestModalHeader">
              <div>
                <p className="adminGuestFamilySummary__eyebrow">
                  {selectedFamily.side}
                </p>
                <h3 className="adminGuestFamilySummary__guestModalTitle">
                  {selectedFamily.familyName}
                </h3>
                <p className="adminGuestFamilySummary__guestModalSubtitle">
                  {selectedFamily.guestCount}{" "}
                  {selectedFamily.guestCount === 1 ? "guest" : "guests"}
                </p>
              </div>

              <button
                type="button"
                className="adminGuestFamilySummary__guestModalClose"
                onClick={() => setSelectedFamily(null)}
              >
                ×
              </button>
            </div>

            <div className="adminGuestFamilySummary__guestModalBody">
              {selectedFamily.guests.length === 0 ? (
                <p className="adminGuestFamilySummary__empty">
                  Guest names are not available inside this family document.
                </p>
              ) : (
                <ul className="adminGuestFamilySummary__guestList">
                  {selectedFamily.guests.map((guest, index) => (
                    <li
                      key={`${guest.id}-${index}`}
                      className="adminGuestFamilySummary__guestItem"
                    >
                      <span>{guest.name}</span>

                      {guest.liquorStatus && <small>{guest.liquorStatus}</small>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AdminGuestFamilySummary;